import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Eye, EyeOff, ShieldCheck, ArrowLeft } from 'lucide-react';
import { tenantName, tenantLogo } from '../config/tenant';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const login = useAuthStore((state) => state.login);
  const verifyMfa = useAuthStore((state) => state.verifyMfa);
  const mfaPending = useAuthStore((state) => state.mfaPending);
  const clearMfaState = useAuthStore((state) => state.clearMfaState);
  const navigate = useNavigate();

  const mfaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mfaPending && mfaInputRef.current) {
      mfaInputRef.current.focus();
    }
  }, [mfaPending]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // If mfaPending is set, login() returns without navigating
      const state = useAuthStore.getState();
      if (state.isAuthenticated) {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyMfa(mfaCode);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    clearMfaState();
    setMfaCode('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ems-bg">
      <div className="w-full max-w-xl px-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-12">
          {/* Logo */}
          <div className="text-center mb-10">
            <img
              src={tenantLogo}
              alt={tenantName}
              className="w-72 h-72 mx-auto mb-6 object-contain"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">
              {error}
            </div>
          )}

          {mfaPending ? (
            /* Step 2: MFA Verification */
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="text-center mb-2">
                <ShieldCheck className="w-10 h-10 text-ems-green mx-auto mb-2" />
                <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Verification Code
                </label>
                <input
                  ref={mfaInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-center text-lg tracking-widest placeholder:text-gray-400 placeholder:text-base placeholder:tracking-normal outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                  placeholder="000000"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || mfaCode.length < 6}
                className="w-full py-2.5 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 font-semibold transition-colors hover:bg-ems-forest/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <div className="text-center space-y-1">
                <p className="text-xs text-gray-500">
                  You can also use a backup code
                </p>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center gap-1 text-sm text-ems-green hover:underline"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to login
                </button>
              </div>
            </form>
          ) : (
            /* Step 1: Email + Password */
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-900">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-ems-green hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-ems-green focus:ring-2 focus:ring-ems-green/20 pr-10"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 font-semibold text-base transition-colors hover:bg-ems-forest/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}
        </div>

        {/* Powered by DJIN badge */}
        <div className="mt-6 flex items-center justify-center">
          <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl border border-gray-200">
            <img
              src="/djin-logo.png"
              alt="DJIN Energy Management Systems"
              className="w-9 h-9 rounded-full object-contain"
            />
            <div>
              <p className="text-xs text-gray-500 font-medium">Powered by</p>
              <p className="text-sm font-bold text-gray-900">DJIN Energy Management Systems</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
